import React, { useEffect, useId, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { Badge, Banner, Button } from '../kit';
import { SKIP_REASON_TEXT, errorMessage, plural, signalsText } from './skillsLogic';
import { runAiSkillReflection } from './skillsApi';

/**
 * "Reflect now": force a reflection on one thread. Runs a model call on the
 * server, so it can take a while; the result (decision, reason, proposals)
 * is shown in place.
 */
export default function ReflectDialog({ open, onClose, onDone, onOpenSkill }) {
  const uid = useId();
  const [threadId, setThreadId] = useState('');
  const [userId, setUserId] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) { setResult(null); setError(null); setRunning(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !running) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, running, onClose]);

  if (!open) return null;

  const run = async (e) => {
    e?.preventDefault();
    if (!threadId.trim() || running) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await runAiSkillReflection(threadId.trim(), userId.trim() || null);
      const r = res?.result || {};
      setResult(r);
      onDone && onDone(r);
    } catch (err) {
      setError(errorMessage(err, 'Reflection failed'));
    } finally {
      setRunning(false);
    }
  };

  const proposed = [...(result?.createdIds || []), ...(result?.updatedIds || [])];

  return ReactDOM.createPortal(
    <div className="ak-layer ak-dialog-wrap" onClick={running ? undefined : onClose}>
      <form
        className="ak-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Reflect on a thread"
        onClick={(e) => e.stopPropagation()}
        onSubmit={run}
      >
        <div className="ak-dialog-body">
          <h2>Reflect on a thread</h2>
          <p style={{ margin: 0 }}>
            Runs the reflection on one conversation now, skipping the usual turn-count and &ldquo;nothing went wrong&rdquo; gates.
            It makes a model call, so it can take up to a minute. Anything it proposes lands in <strong>Proposed</strong> for review.
          </p>
          <div className="ak-field">
            <label className="ak-label" htmlFor={`${uid}-thread`}>Thread id<span className="ak-req" aria-hidden="true">*</span></label>
            <input id={`${uid}-thread`} className="ak-input ak-mono" value={threadId} autoFocus disabled={running} onChange={(e) => setThreadId(e.target.value)} />
          </div>
          <div className="ak-field">
            <label className="ak-label" htmlFor={`${uid}-user`}>Thread owner (optional)</label>
            <input id={`${uid}-user`} className="ak-input ak-mono" value={userId} disabled={running} placeholder="Defaults to you" onChange={(e) => setUserId(e.target.value)} />
            <span className="ak-help">Chat threads are stored per user. Leave blank for one of your own threads; otherwise enter the owner&apos;s user id.</span>
          </div>

          {running ? <div className="sk-reflect-running" role="status">Reflecting… this runs a model call.</div> : null}
          {error ? <Banner tone="bad" icon={AlertTriangle} title="Reflection failed">{error}</Banner> : null}
          {result ? (
            <div className="sk-reflect-result" role="status">
              {result.ran ? (
                <>
                  <div className="sk-reflect-line">
                    <Badge tone={result.decision === 'propose' ? 'warn' : undefined}>
                      {result.decision === 'propose' ? 'Proposed' : 'No skill needed'}
                    </Badge>
                    <span className="sk-muted-sm">
                      {plural(result.turns || 0, 'turn')}{result.signals ? ` · ${signalsText(result.signals)}` : ''}{result.model ? ` · ${result.model}` : ''}
                    </span>
                  </div>
                  {result.reason ? <p style={{ margin: 0 }}>{result.reason}</p> : null}
                  {proposed.length ? (
                    <div className="sk-reflect-line">
                      {proposed.map((id) => (
                        <Button key={id} size="small" onClick={() => { onOpenSkill(id); onClose(); }}>
                          {(result.updatedIds || []).includes(id) ? 'Open updated proposal' : 'Open proposal'}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <Badge>Skipped</Badge>
                  <p style={{ margin: 0 }}>
                    {SKIP_REASON_TEXT[result.skipped] || `Skipped (${result.skipped || 'unknown reason'}).`}
                    {result.error ? ` ${result.error}` : ''}
                  </p>
                </>
              )}
            </div>
          ) : null}
        </div>
        <div className="ak-dialog-foot">
          <Button onClick={onClose} disabled={running}>{result ? 'Close' : 'Cancel'}</Button>
          <Button variant="primary" type="submit" disabled={running || !threadId.trim()}>
            {running ? 'Reflecting…' : result ? 'Run again' : 'Reflect now'}
          </Button>
        </div>
      </form>
    </div>,
    document.body
  );
}
